import React, { useEffect, useState } from 'react';

export default function Step1BasicInfo({ formData, dropdowns, onNext, onChange }) {
  const [countries, setCountries] = useState([]);

  const defaultPedigree = [
    { relation: 'Sire', name: '' },
    { relation: 'Dam', name: '' },
    { relation: "Grandsire (Sire's side)", name: '' },
    { relation: "Grandsire (Dam's side)", name: '' },
  ];

  useEffect(() => {
    if (!formData.horseInfo?.pedigree || formData.horseInfo.pedigree.length !== defaultPedigree.length) {
      onChange({
        horseInfo: {
          ...formData.horseInfo,
          pedigree: defaultPedigree
        }
      });
    }
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('https://restcountries.com/v3.1/all');
        const data = await response.json();
        const countryNames = data.map((country) => country.name.common);
        const prioritized = ['United States', 'United Kingdom', 'Canada'];
        const sortedCountries = [
          ...prioritized,
          ...countryNames
            .filter((name) => !prioritized.includes(name))
            .sort(),
        ];
        setCountries(sortedCountries);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };

    fetchCountries();
  }, []);

  const handleInput = (e) => {
    const { name, value, dataset } = e.target;

    if (name === 'pedigree') {
      const index = parseInt(dataset.index, 10);
      const updatedPedigree = [...(formData.horseInfo?.pedigree || [])];
      updatedPedigree[index] = {
        ...updatedPedigree[index],
        name: value,
      };
      onChange({
        horseInfo: {
          ...formData.horseInfo,
          pedigree: updatedPedigree
        }
      });
    } else if (['city', 'state', 'country', 'zip'].includes(name)) {
      onChange({
        horseInfo: {
          ...formData.horseInfo,
          location: {
            ...(formData.horseInfo?.location || {}),
            details: {
              ...(formData.horseInfo?.location?.details || {}),
              [name]: value,
            },
          },
        },
      });
    } else {
      onChange({
        horseInfo: {
          ...formData.horseInfo,
          [name]: value
        }
      });
    }
  };

  const pedigreeData = Array.isArray(formData.horseInfo?.pedigree) && formData.horseInfo.pedigree.length === 4
    ? formData.horseInfo.pedigree
    : defaultPedigree;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-4">Basic Horse Information</h2>

      {/* Horse Name */}
      <div>
        <label className="block text-sm font-bold">Horse Name</label>
        <input
          type="text"
          name="name"
          value={formData.horseInfo?.name || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      {/* Horse Location */}
      <h3 className="font-bold">Horse Location</h3>
      <div className="columns-md">
        <label className="block text-sm font-bold">City</label>
        <input
          type="text"
          name="city"
          value={formData.horseInfo?.location?.details?.city || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        />

        <label className="block text-sm font-bold">State</label>
        <input
          type="text"
          name="state"
          value={formData.horseInfo?.location?.details?.state || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      <div className="columns-md">
        <label className="block text-sm font-bold">Country</label>
        <select
          name="country"
          value={formData.horseInfo?.location?.details?.country || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">Select</option>
          {countries.map((country) => (
            <option key={country} value={country}>{country}</option>
          ))}
        </select>

        <label className="block text-sm font-bold">Zip Code</label>
        <input
          type="text"
          name="zip"
          value={formData.horseInfo?.location?.details?.zip || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      {/* Age and Breed */}
      <div className="columns-md">
        <label className="block text-sm font-bold">Age</label>
        <select
          name="age"
          value={formData.horseInfo?.age || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">Select</option>
          {dropdowns.ageRange?.map((age) => (
            <option key={age} value={age}>{age}</option>
          ))}
        </select>

        <label className="block text-sm font-bold">Breed</label>
        <select
          name="breed"
          value={formData.horseInfo?.breed || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">Select</option>
          {dropdowns.breeds?.map((breed) => (
            <option key={breed._id} value={breed._id}>{breed.name}</option>
          ))}
        </select>
      </div>

      {/* Sex and Height */}
      <div className="columns-md">
        <label className="block text-sm font-bold">Sex</label>
        <select
          name="sex"
          value={formData.horseInfo?.sex || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">Select</option>
          {dropdowns.sex?.map((sex) => (
            <option key={sex} value={sex}>{sex}</option>
          ))}
        </select>

        <label className="block text-sm font-bold">Height (hands)</label>
        <select
          name="height"
          value={formData.horseInfo?.height || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">Select</option>
          {dropdowns.height?.map((height) => (
            <option key={height} value={height}>{height}</option>
          ))}
        </select>
      </div>

      {/* Color */}
      <div>
        <label className="block text-sm font-bold">Color</label>
        <select
          name="color"
          value={formData.horseInfo?.color || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">Select</option>
          {dropdowns.color?.map((color) => (
            <option key={color} value={color}>{color}</option>
          ))}
        </select>
      </div>

      {/* Pedigree */}
      <h3 className="font-bold">Pedigree</h3>
      <div className="columns-md">
        {pedigreeData.map((ancestor, index) => (
          <div key={index}>
            <label className="block text-sm font-bold">{ancestor.relation}</label>
            <input
              type="text"
              name="pedigree"
              data-index={index}
              value={ancestor.name}
              onChange={handleInput}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        ))}
      </div>

      {/* Horse Description */}
      <div>
        <label className="block text-sm font-bold">Horse Description</label>
        <textarea
          name="description"
          value={formData.horseInfo?.description || ''}
          onChange={handleInput}
          className="w-full border rounded px-3 py-2 h-28"
          placeholder="Tell buyers about the horse’s temperament, training, background, or notable history..."
        />
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={onNext}
          className="bg-primary text-white py-2 px-6 rounded hover:bg-primary-dark"
        >
          Next
        </button>
      </div>
    </div>
  );
}